import { customizeError } from '@chilecompra/react-kit'
import { formatDate } from 'date-fns'

import { YES_NO_DICTIONARY } from '../../config/settings/constants'
import { makeActionCreator } from '../../config/store/utils'
import {
  createCfmp,
  fetchAdministrativeActTypes,
  fetchCurrencies,
  fetchProcedureTypes,
  fetchReasonTypes
} from '../../services/cfmp'
import { onShowSnackbar } from '../SnackbarProvider'

/**
 * Utils
 */
const buildErrorPayload = error => ({
  error: customizeError({
    code: error.code,
    originalError: error.originalError,
    reason: 'SOMETHING_WENT_WRONG_ERROR'
  }).toObject()
})

const toBoolean = value => value === YES_NO_DICTIONARY.YES

const mapToSelectOptions = payload =>
  Array.isArray(payload)
    ? payload.map(({ description, id, isOther }) => ({
        isOther: Boolean(isOther),
        name: description,
        value: id
      }))
    : []

const mapFormToCfmpPayload = cfmp => {
  const basePayload = {
    administrativeActDate: formatDate(cfmp.administrativeActDate, 'dd/MM/yyyy'),
    administrativeActName: cfmp.administrativeActName,
    administrativeActNumber: cfmp.administrativeActNumber,
    administrativeActTypeDetail: cfmp.administrativeActTypeDetail || null,
    administrativeActTypeId: cfmp.administrativeActType,
    contractEndDate: formatDate(cfmp.contractEndDate, 'dd/MM/yyyy'),
    contractPurpose: cfmp.purchaseReason,
    contractStartDate: formatDate(cfmp.contractStartDate, 'dd/MM/yyyy'),
    currencyType: cfmp.currency,
    environmentalCriteria: toBoolean(cfmp.environmentalPurchase),
    procedureTypeDetail: cfmp.procedureTypeDetail || null,
    procedureTypeId: cfmp.procedureType,
    reasonId: cfmp.reasonType,
    socialEconomicImpact: toBoolean(cfmp.socialEconomicPurchase),
    supplierLegalName: cfmp.supplierName,
    supplierTaxId: cfmp.supplierRut,
    totalAmount: cfmp.totalAmount
  }

  return basePayload
}

export const onGetCfmpSourcesThunk = () => async dispatch => {
  await Promise.all([
    dispatch(onGetCurrenciesThunk()),
    dispatch(onGetReasonTypesThunk()),
    dispatch(onGetProcedureTypesThunk()),
    dispatch(onGetAdministrativeActTypesThunk())
  ])
}

/**
 * Cfmp actions
 */
export const ADD_FILE_SUCCESS = 'ADD_FILE_SUCCESS'
export const onAddFileSuccess = makeActionCreator(ADD_FILE_SUCCESS, 'payload')
export const onAddFileThunk =
  ({ categoryId, file }) =>
  async dispatch =>
    dispatch(onAddFileSuccess({ categoryId, file, fileName: file.name }))

export const CLOSE_MORE_INFO_DIALOG = 'CLOSE_MORE_INFO_DIALOG'
export const onCloseMoreInfoDialog = makeActionCreator(CLOSE_MORE_INFO_DIALOG)

export const CREATE_CFMP = 'CREATE_CFMP'
export const CREATE_CFMP_ERROR = 'CREATE_CFMP_ERROR'
export const CREATE_CFMP_SUCCESS = 'CREATE_CFMP_SUCCESS'
export const onCreateCfmp = makeActionCreator(CREATE_CFMP)
export const onCreateCfmpError = makeActionCreator(CREATE_CFMP_ERROR, 'payload')
export const onCreateCfmpSuccess = makeActionCreator(CREATE_CFMP_SUCCESS, 'payload')
export const onCreateCfmpThunk =
  ({ attachedFiles, callbackFormReset, cfmp }) =>
  async (dispatch, getState) => {
    dispatch(onCreateCfmp())

    try {
      const { data: reasonTypes } = getState().cfmp.reasonTypes

      const cfmpPayload = mapFormToCfmpPayload(cfmp)

      const { data } = await createCfmp({
        attachedFiles,
        cfmp: {
          ...cfmpPayload
        }
      })

      dispatch(
        onShowSnackbar({
          message: 'Tu solicitud de Compra Fuera de Mercado Público fue ingresada correctamente.',
          severity: 'success'
        })
      )

      callbackFormReset()

      const currentCfmp = {
        amount: cfmp.totalAmount || '',
        code: data.payload.cfmpCode || '',
        createdAt: formatDate(cfmp.contractStartDate, 'dd/MM/yyyy') || '',
        reason: reasonTypes.find(({ value }) => value === cfmp.reasonType)?.name || '',
        rut: cfmp.supplierRut || '',
        supplier: cfmp.supplierName || ''
      }

      return dispatch(onCreateCfmpSuccess({ cfmp: currentCfmp }))
    } catch (error) {
      dispatch(
        onShowSnackbar({
          message: 'No fue posible ingresar la solicitud. Por favor, inténtalo nuevamente.',
          severity: 'error'
        })
      )

      return dispatch(onCreateCfmpError(buildErrorPayload(error)))
    }
  }

export const GET_ADMINISTRATIVE_ACT_TYPES = 'GET_ADMINISTRATIVE_ACT_TYPES'
export const GET_ADMINISTRATIVE_ACT_TYPES_ERROR = 'GET_ADMINISTRATIVE_ACT_TYPES_ERROR'
export const GET_ADMINISTRATIVE_ACT_TYPES_SUCCESS = 'GET_ADMINISTRATIVE_ACT_TYPES_SUCCESS'
export const onGetAdministrativeActTypes = makeActionCreator(GET_ADMINISTRATIVE_ACT_TYPES)
export const onGetAdministrativeActTypesError = makeActionCreator(
  GET_ADMINISTRATIVE_ACT_TYPES_ERROR,
  'payload'
)
export const onGetAdministrativeActTypesSuccess = makeActionCreator(
  GET_ADMINISTRATIVE_ACT_TYPES_SUCCESS,
  'payload'
)
export const onGetAdministrativeActTypesThunk = () => async dispatch => {
  dispatch(onGetAdministrativeActTypes())

  try {
    const { data } = await fetchAdministrativeActTypes()

    const administrativeActTypes = mapToSelectOptions(data?.payload)

    return dispatch(onGetAdministrativeActTypesSuccess({ administrativeActTypes }))
  } catch (error) {
    return dispatch(onGetAdministrativeActTypesError(buildErrorPayload(error)))
  }
}

export const GET_CURRENCIES = 'GET_CURRENCIES'
export const GET_CURRENCIES_ERROR = 'GET_CURRENCIES_ERROR'
export const GET_CURRENCIES_SUCCESS = 'GET_CURRENCIES_SUCCESS'
export const onGetCurrencies = makeActionCreator(GET_CURRENCIES)
export const onGetCurrenciesError = makeActionCreator(GET_CURRENCIES_ERROR, 'payload')
export const onGetCurrenciesSuccess = makeActionCreator(GET_CURRENCIES_SUCCESS, 'payload')
export const onGetCurrenciesThunk = () => async dispatch => {
  dispatch(onGetCurrencies())

  try {
    const { data } = await fetchCurrencies()

    const currencies =
      data?.payload?.map(item => ({
        decimals: item.decimals,
        name: `${item.currency} - ${item.name}`,
        value: item.currency
      })) || []

    return dispatch(onGetCurrenciesSuccess({ currencies }))
  } catch (error) {
    return dispatch(onGetCurrenciesError(buildErrorPayload(error)))
  }
}

export const GET_PROCEDURE_TYPES = 'GET_PROCEDURE_TYPES'
export const GET_PROCEDURE_TYPES_ERROR = 'GET_PROCEDURE_TYPES_ERROR'
export const GET_PROCEDURE_TYPES_SUCCESS = 'GET_PROCEDURE_TYPES_SUCCESS'
export const onGetProcedureTypes = makeActionCreator(GET_PROCEDURE_TYPES)
export const onGetProcedureTypesError = makeActionCreator(GET_PROCEDURE_TYPES_ERROR, 'payload')
export const onGetProcedureTypesSuccess = makeActionCreator(GET_PROCEDURE_TYPES_SUCCESS, 'payload')
export const onGetProcedureTypesThunk = () => async dispatch => {
  dispatch(onGetProcedureTypes())

  try {
    const { data } = await fetchProcedureTypes()

    const procedureTypes = mapToSelectOptions(data?.payload)

    return dispatch(onGetProcedureTypesSuccess({ procedureTypes }))
  } catch (error) {
    return dispatch(onGetProcedureTypesError(buildErrorPayload(error)))
  }
}

export const GET_REASON_TYPES = 'GET_REASON_TYPES'
export const GET_REASON_TYPES_ERROR = 'GET_REASON_TYPES_ERROR'
export const GET_REASON_TYPES_SUCCESS = 'GET_REASON_TYPES_SUCCESS'
export const onGetReasonTypes = makeActionCreator(GET_REASON_TYPES)
export const onGetReasonTypesError = makeActionCreator(GET_REASON_TYPES_ERROR, 'payload')
export const onGetReasonTypesSuccess = makeActionCreator(GET_REASON_TYPES_SUCCESS, 'payload')
export const onGetReasonTypesThunk = () => async dispatch => {
  dispatch(onGetReasonTypes())

  try {
    const { data } = await fetchReasonTypes()

    const reasonTypes = mapToSelectOptions(data?.payload)

    return dispatch(onGetReasonTypesSuccess({ reasonTypes }))
  } catch (error) {
    return dispatch(onGetReasonTypesError(buildErrorPayload(error)))
  }
}

export const OPEN_MORE_INFO_DIALOG = 'OPEN_MORE_INFO_DIALOG'
export const onOpenMoreInfoDialog = makeActionCreator(OPEN_MORE_INFO_DIALOG)

export const REMOVE_FILE_SUCCESS = 'REMOVE_FILE_SUCCESS'
export const onRemoveFileSuccess = makeActionCreator(REMOVE_FILE_SUCCESS, 'payload')

export const RESET_CFMP = 'RESET_CFMP'
export const onResetCfmp = makeActionCreator(RESET_CFMP)
